import { formatOrderAddress } from "@repo/shopify/utils/address";
import { extractId } from "@repo/shopify/utils/common";
import type { MailingAddress } from "@shopify/hydrogen-react/storefront-api-types";
import classNames from "classnames";
import { t } from "i18next";
import React, { useEffect, useMemo, useState } from "react";

import Button from "@atoms/Button";
import Input from "@atoms/Input";

import Address from "@molecules/Address";

import AddressForm from "@organisms/Address/AddressForm";
import AddressFormIntl from "@organisms/Address/AddressFormIntl";

import useCustomerQuery from "@hooks/useCustomerQuery";
import useLanguage from "@hooks/useLanguage";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

interface IProps {
  addressList: MailingAddress[] | [];
  selectedAddress?: MailingAddress;
  selectedAddressId: string;
  setSelectedAddressId: React.Dispatch<React.SetStateAction<string>>;
  isTossPartnership: boolean;
  isAddressMutateLoading?: boolean;
  setIsAddressMutateLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  isAddressChangeLoading?: boolean;
  setIsAddressChangeLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}

function ShippingAddress({
  addressList,
  selectedAddress,
  selectedAddressId,
  setSelectedAddressId,
  isTossPartnership,
  isAddressMutateLoading,
  setIsAddressMutateLoading,
  isAddressChangeLoading,
  setIsAddressChangeLoading,
}: IProps) {
  const { lang } = useLanguage();
  const { customer, token } = useCustomerStore();
  const { openModal, setIsLoading: setIsModalLoading } = useLayoutStore();

  const {
    fetchCustomer,
    deleteAddress,
    setDefaultAddressLoading,
    createAddressLoading,
    editAddressLoading,
    deleteAddressLoading,
  } = useCustomerQuery();

  const [showAddressList, setShowAddressList] = useState<boolean>(false);
  const [onCompletedInProgress, setOnCompletedInProgress] = useState<boolean>(false);

  const isLoading = useMemo(() => {
    return createAddressLoading || editAddressLoading || setDefaultAddressLoading || deleteAddressLoading;
  }, [createAddressLoading, editAddressLoading, setDefaultAddressLoading, deleteAddressLoading]);

  useEffect(() => {
    if (isLoading || onCompletedInProgress) setIsAddressMutateLoading?.(true);
    else setIsAddressMutateLoading?.(false);
  }, [isLoading, onCompletedInProgress]);

  /** Update selectedAddressId after address mutation(Create/Update/Delete) **/
  const onCompletedMutation = async (addressId?: string) => {
    setOnCompletedInProgress(true);
    await fetchCustomer({ variables: { customerAccessToken: token.accessToken } }).then((res) => {
      if (!res?.data) return;

      // 1. Fetch customer
      const { customer: fetchedCustomer } = res.data;
      const { addresses, defaultAddress } = fetchedCustomer;

      // 2. Update selected address
      const hasAddresses = addresses?.edges?.length > 0;
      if (!hasAddresses || !defaultAddress?.id) {
        // 2-1. No address available
        setSelectedAddressId("");
      } else {
        // 2-2. Default address exists
        setSelectedAddressId(addressId ?? defaultAddress.id);
      }

      // 3. Show address list
      setShowAddressList(true);
    });
    setOnCompletedInProgress(false);
  };

  /** Show deleteAddress modal **/
  const handleDeleteAddress = (id: string) => {
    openModal({
      title: "Remove Address",
      content: <p>{t("addresses.delete_address")}</p>,
      confirm: {
        label: "Delete",
        onClick: () => {
          setIsModalLoading(true);
          setOnCompletedInProgress(true);
          void deleteAddress({ variables: { id } }).then(async () => {
            await onCompletedMutation?.(selectedAddressId);
            setIsModalLoading(false);
            setOnCompletedInProgress(false);
            openModal(null);
          });
        },
      },
      cancel: {
        label: "Cancel",
        onClick: () => {
          openModal(null);
        },
      },
    });
  };

  /** Show address form **/
  const handleOpenAddressForm = (editableAddress: MailingAddress | undefined) => {
    const title = !editableAddress ? "Add a New Address" : "Edit a Address";

    openModal({
      title,
      content:
        lang === "ko" ? (
          <AddressForm
            address={editableAddress}
            isDefaultAddress={extractId(customer?.defaultAddress?.id ?? "") === extractId(editableAddress?.id ?? "")}
            onClickCancel={() => {
              openModal(null);
            }}
            onCompletedMutation={onCompletedMutation}
          />
        ) : (
          <AddressFormIntl
            address={editableAddress}
            className="tnt-gap-x grid grid-cols-8 gap-y-2.5"
            isDefaultAddress={extractId(customer?.defaultAddress?.id ?? "") === extractId(editableAddress?.id ?? "")}
            onClickCancel={() => {
              openModal(null);
            }}
            onCompletedMutation={onCompletedMutation}
          />
        ),
      isHideCancelButton: true,
    });
  };

  // 배송지 정보
  const shippingAddress = formatOrderAddress({ address: selectedAddress, lang });

  return (
    <div className="flex flex-col gap-2.5">
      <div className={classNames("flex", (addressList?.length ?? 0) > 0 ? "justify-between" : "flex-col gap-5")}>
        <h2>Shipping Address</h2>

        {/* New address */}
        {customer?.addresses?.edges?.length === 0 ? (
          <div className="flex flex-col gap-5">
            <span>{t("checkout.create_shipping_address")}</span>
            <Button
              as="square"
              onClick={() => {
                handleOpenAddressForm(undefined);
              }}
            >
              {t("addresses.create_new_address")}
            </Button>
          </div>
        ) : null}

        {/* Toggle button */}
        {selectedAddress ? (
          <Button
            as="text"
            onClick={() => {
              if (!showAddressList) {
                setShowAddressList(true);
              } else if (showAddressList) {
                setShowAddressList(false);
              }
            }}
          >
            {!showAddressList ? t("addresses.use_other") : "Close"}
          </Button>
        ) : null}
      </div>

      {/* Default address */}
      {selectedAddress && !showAddressList ? <Address {...shippingAddress} /> : null}

      {/* Address list */}
      {showAddressList && (addressList?.length ?? 0) > 0 ? (
        <>
          <ul className="flex flex-col gap-5">
            {addressList?.map((node) => {
              const address = formatOrderAddress({ address: node, lang });

              return (
                <li className="flex flex-col gap-2.5" key={node.id}>
                  <div className="flex gap-1">
                    <Input
                      checked={extractId(selectedAddressId) === extractId(node.id)}
                      id={node.id}
                      label={<Address {...address} />}
                      name="address"
                      onChange={() => {
                        if (isAddressMutateLoading || isAddressChangeLoading) return;

                        if (extractId(selectedAddressId) !== extractId(node.id)) {
                          setIsAddressChangeLoading?.(true);
                        }
                        setSelectedAddressId(node.id);
                      }}
                      type="radio"
                      value={node.id}
                    />
                  </div>
                  <div className="ml-5 flex gap-2.5">
                    <Button
                      as="text"
                      onClick={() => {
                        if (isAddressMutateLoading || isAddressChangeLoading) return;
                        handleOpenAddressForm(node);
                      }}
                    >
                      Edit
                    </Button>
                    {!isTossPartnership && (
                      <Button
                        as="text"
                        onClick={() => {
                          if (isAddressMutateLoading || isAddressChangeLoading) return;
                          handleDeleteAddress(node.id);
                        }}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          {!isTossPartnership && (
            <Button
              as="square"
              loading={isAddressMutateLoading || isAddressChangeLoading || false}
              onClick={() => {
                if (isAddressMutateLoading || isAddressChangeLoading) return;
                handleOpenAddressForm(undefined);
              }}
            >
              {t("addresses.create_new_address")}
            </Button>
          )}
        </>
      ) : null}
    </div>
  );
}

export default ShippingAddress;
